<template>
  <span class="artist-external-stats">
    <b-spinner v-if="loading && !stats" small class="text-primary" />

    <template v-else-if="stats">
      <!-- Spotify: популярность 0–100 на фирменном зелёном бейдже -->
      <b-link
          v-if="stats.spotify"
          :href="stats.spotify.url"
          :disabled="!stats.spotify.url"
          target="_blank"
          rel="noopener noreferrer"
          :title="spotifyTitle"
      >
        <span class="artist-external-stats__badge artist-external-stats__badge--spotify">
          <source-icon source="spotify" color="#fff" :size="14" />
          {{ stats.spotify.popularity !== null ? stats.spotify.popularity : '—' }}
        </span>
      </b-link>
      <span
          v-else
          class="artist-external-stats__badge artist-external-stats__badge--empty"
          :class="{ 'artist-external-stats__badge--error': hasError('spotify') }"
          :title="hasError('spotify') ? 'Spotify: ошибка запроса' : 'Нет в Spotify'"
      >
        <source-icon source="spotify" color="currentColor" :size="14" />
        {{ hasError('spotify') ? '!' : '—' }}
      </span>

      <!-- Last.fm: слушатели на фирменном красном бейдже -->
      <b-link
          v-if="stats.lastfm"
          :href="stats.lastfm.url"
          target="_blank"
          rel="noopener noreferrer"
          :title="lastfmTitle"
      >
        <span class="artist-external-stats__badge artist-external-stats__badge--lastfm">
          <source-icon source="lastfm" color="#fff" :size="14" />
          {{ formatCompactNumber(stats.lastfm.listeners) }}
        </span>
      </b-link>
      <span
          v-else
          class="artist-external-stats__badge artist-external-stats__badge--empty"
          :class="{ 'artist-external-stats__badge--error': hasError('lastfm') }"
          :title="hasError('lastfm') ? 'Last.fm: ошибка запроса' : 'Нет в Last.fm'"
      >
        <source-icon source="lastfm" color="currentColor" :size="14" />
        {{ hasError('lastfm') ? '!' : '—' }}
      </span>

      <!-- Поиск Spotify нечёткий: найденное имя может быть чужим -->
      <span
          v-if="isNameMismatch"
          class="text-warning font-small-2 text-nowrap"
          :title="`Spotify нашёл другого артиста: ${stats.spotify.name}`"
      >
        <feather-icon icon="AlertTriangleIcon" size="12" />
        {{ stats.spotify.name }}
      </span>
    </template>
  </span>
</template>

<script>
import { BLink, BSpinner } from 'bootstrap-vue'
import SourceIcon from '@/views/db-music/SourceIcon.vue'

// Сравнение имён без регистра и пробелов
const normalizeName = name => String(name || '').toLowerCase().replace(/\s+/g, '')

// Дробная часть с запятой и без «,0»: 1.5 → «1,5», 2.0 → «2»
const formatFraction = value => (value < 10 ? value.toFixed(1) : String(Math.round(value)))
  .replace(/\.0$/, '')
  .replace('.', ',')

/** «1,5 млрд», «1,5 млн», «312 тыс.», «950» */
export const formatCompactNumber = value => {
  if (value === null || value === undefined) return '—'
  const number = Number(value)
  if (Number.isNaN(number)) return '—'
  if (number >= 1e9) return `${formatFraction(number / 1e9)} млрд`
  if (number >= 1e6) return `${formatFraction(number / 1e6)} млн`
  if (number >= 1e3) return `${formatFraction(number / 1e3)} тыс.`
  return String(number)
}

export default {
  name: 'ArtistExternalStatsBadge',
  components: {
    BLink,
    BSpinner,
    SourceIcon,
  },
  props: {
    // Элемент ответа /generator/artists/external-stats: { name, spotify, lastfm, errors }
    stats: {
      type: Object,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Имя, по которому искали, — для предупреждения о несовпадении с найденным в Spotify
    originalName: {
      type: String,
      default: '',
    },
  },
  computed: {
    // Подписи убраны из бейджей — детали в подсказке при наведении
    spotifyTitle() {
      const spotify = this.stats.spotify
      return `Spotify: ${spotify.name}\nПопулярность: ${spotify.popularity !== null ? spotify.popularity : '—'} из 100\n`
        + `Подписчиков: ${formatCompactNumber(spotify.followers)}`
    },
    lastfmTitle() {
      const lastfm = this.stats.lastfm
      return `Last.fm\nСлушателей: ${formatCompactNumber(lastfm.listeners)}\n`
        + `Прослушиваний: ${formatCompactNumber(lastfm.playcount)}`
    },
    isNameMismatch() {
      return Boolean(this.stats && this.stats.spotify && this.stats.spotify.name && this.originalName)
        && normalizeName(this.stats.spotify.name) !== normalizeName(this.originalName)
    },
  },
  methods: {
    formatCompactNumber,
    hasError(source) {
      return Boolean(this.stats && Array.isArray(this.stats.errors) && this.stats.errors.includes(source))
    },
  },
}
</script>

<style lang="scss" scoped>
.artist-external-stats {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-left: 0.5rem;
  vertical-align: middle;
}

.artist-external-stats__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  border-radius: 0.358rem;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  color: #fff;

  &--spotify {
    background-color: #1db954;
  }

  &--lastfm {
    background-color: #d51007;
  }

  &--empty {
    color: #b9b9c3;
    background-color: rgba(130, 134, 139, 0.12);
  }

  &--error {
    color: #ea5455;
  }
}
</style>
